"use client";

import { Fragment, type ReactNode } from "react";
import { Languages, Loader2, RefreshCw } from "lucide-react";

export type LessonPart = "not_ready" | "missing" | "stale" | "translated";
export type QuizPart = "none" | "blocked" | "missing" | "stale" | "translated";
export type TranslationScope = "lessons" | "quizzes" | "placement" | "lesson";

export type LessonTranslationRow = {
  lessonId: number;
  lessonTitle: string;
  moduleId: number;
  moduleTitle: string;
  published: boolean;
  lesson: LessonPart;
  quiz: QuizPart;
  sourceCanonicalId: number | null;
};

export type TranslationStatus = {
  language: string;
  offered: boolean;
  lessons: { total: number; translated: number; stale: number; missing: number; notReady: number };
  quizzes: { total: number; translated: number; stale: number; missing: number; blocked: number };
  placement: { total: number; translated: number };
  readyToOffer: boolean;
  blocking: string[];
  rows: LessonTranslationRow[];
};

type ChipTone = "done" | "warn" | "todo" | "none";

const CHIP_CLASS: Record<ChipTone, string> = {
  done: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  warn: "bg-amber-50 text-amber-800 ring-amber-200",
  todo: "bg-slate-100 text-slate-600 ring-slate-200",
  none: "text-slate-400",
};

function Chip({ tone, children }: { tone: ChipTone; children: ReactNode }) {
  if (tone === "none") return <span className="text-xs text-slate-400">{children}</span>;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${CHIP_CLASS[tone]}`}
    >
      {children}
    </span>
  );
}

const LESSON_CHIP: Record<LessonPart, { tone: ChipTone; label: string }> = {
  translated: { tone: "done", label: "Translated" },
  stale: { tone: "warn", label: "Out of date" },
  missing: { tone: "todo", label: "Not translated" },
  not_ready: { tone: "none", label: "No English content" },
};

const QUIZ_CHIP: Record<QuizPart, { tone: ChipTone; label: string }> = {
  translated: { tone: "done", label: "Translated" },
  stale: { tone: "warn", label: "Out of date" },
  missing: { tone: "todo", label: "Not translated" },
  blocked: { tone: "todo", label: "Needs its lesson first" },
  none: { tone: "none", label: "No quiz" },
};

/** One artifact type: what is covered, and the button that covers the rest. */
function ActionCard({
  label,
  done,
  total,
  outstanding,
  note,
  actionLabel,
  onAction,
  busy,
}: {
  label: string;
  done: number;
  total: number;
  outstanding: number;
  note?: string;
  actionLabel: string;
  onAction: () => void;
  busy: boolean;
}) {
  const complete = total > 0 && done >= total;
  const pct = total === 0 ? 100 : Math.round((done / total) * 100);
  return (
    <div className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2">
      <span className="w-28 shrink-0 text-sm font-semibold text-slate-700">{label}</span>
      <span
        className={`w-14 shrink-0 text-sm font-bold tabular-nums ${complete ? "text-emerald-700" : "text-slate-700"}`}
      >
        {done}/{total}
      </span>
      <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full rounded-full transition-all ${complete ? "bg-emerald-500" : "bg-indigo-500"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {note ? (
        <span className="hidden shrink-0 text-xs text-slate-500 xl:inline">{note}</span>
      ) : null}
      <button
        type="button"
        onClick={onAction}
        disabled={busy || outstanding === 0}
        title={note}
        className="shrink-0 inline-flex items-center justify-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
        {outstanding === 0 ? "Up to date" : actionLabel}
      </button>
    </div>
  );
}

/**
 * Translation state for a course, and every action that changes it.
 */
export function TranslationsTab({
  statuses,
  loadError,
  onTranslate,
  onSetOffered,
  onSyncStale,
  onRefresh,
  busy,
  refreshing,
}: {
  /** `null` while the status has not loaded. Never legitimately empty. */
  statuses: TranslationStatus[] | null;
  loadError?: string | null;
  onTranslate: (
    language: string,
    scope: TranslationScope,
    opts?: { lessonId?: number; includeStale?: boolean },
  ) => void;
  onSetOffered: (language: string, offered: boolean) => void;
  onSyncStale: (language: string) => void;
  onRefresh: () => void;
  busy: boolean;
  refreshing: boolean;
}) {
  const status = statuses?.[0];

  if (!status) {
    const failed = loadError != null || statuses != null;
    return (
      <div className="rounded-xl border border-slate-200 bg-white px-6 py-10 text-center shadow-sm">
        <Languages className="mx-auto h-7 w-7 text-slate-300" />
        {failed ? (
          <>
            <p className="mt-3 text-sm font-semibold text-slate-800">
              Could not load translation status
            </p>
            <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500">
              {loadError ?? "The server returned no languages."} Nothing is wrong with the
              course itself.
            </p>
            <button
              type="button"
              onClick={onRefresh}
              disabled={refreshing}
              className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
              Try again
            </button>
          </>
        ) : (
          <p className="mt-3 text-sm text-slate-500">Loading translation status…</p>
        )}
      </div>
    );
  }

  const { language, rows } = status;
  const lessonsOutstanding = status.lessons.missing;
  const quizzesOutstanding = status.quizzes.missing;
  const placementOutstanding = status.placement.total - status.placement.translated;
  const outOfDate = status.lessons.stale + status.quizzes.stale;

  // Group by module, preserving the order the API sent them in.
  const byModule: { moduleId: number; moduleTitle: string; rows: LessonTranslationRow[] }[] = [];
  for (const row of rows) {
    const last = byModule[byModule.length - 1];
    if (last && last.moduleId === row.moduleId) last.rows.push(row);
    else byModule.push({ moduleId: row.moduleId, moduleTitle: row.moduleTitle, rows: [row] });
  }

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-slate-100 bg-slate-50/60 px-5 py-3">
        <h2 className="text-base font-bold text-slate-900">Translations</h2>
        <span className="text-base font-bold text-slate-900">·</span>
        <span className="text-base font-bold text-slate-900">{language}</span>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${
            status.offered
              ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
              : "bg-slate-50 text-slate-600 ring-slate-200"
          }`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${status.offered ? "bg-emerald-500" : "bg-slate-400"}`}
          />
          {status.offered ? "Offered to learners" : "Not offered yet"}
        </span>

        <div className="ml-auto flex items-center gap-2">
          {loadError ? (
            <span className="rounded-lg bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-900 ring-1 ring-amber-200">
              Last refresh failed, showing earlier numbers
            </span>
          ) : null}
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing" : "Refresh"}
          </button>
          {status.offered ? (
            <button
              type="button"
              onClick={() => onSetOffered(language, false)}
              disabled={busy}
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
            >
              Stop offering {language}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onSetOffered(language, true)}
              disabled={busy || !status.readyToOffer}
              title={status.readyToOffer ? undefined : status.blocking.join(". ")}
              className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500 disabled:shadow-none"
            >
              <Languages className="h-3.5 w-3.5" />
              Offer {language} to learners
            </button>
          )}
        </div>
      </div>

      <div className="space-y-2 px-5 py-4">
        <ActionCard
          label="Lessons"
          done={status.lessons.translated + status.lessons.stale}
          total={status.lessons.total - status.lessons.notReady}
          outstanding={lessonsOutstanding}
          note={
            status.lessons.notReady > 0
              ? `${status.lessons.notReady} have no English content yet`
              : lessonsOutstanding > 0
                ? `${lessonsOutstanding} still to translate`
                : undefined
          }
          actionLabel={`Translate ${lessonsOutstanding} lesson${lessonsOutstanding === 1 ? "" : "s"}`}
          onAction={() => onTranslate(language, "lessons")}
          busy={busy}
        />
        <ActionCard
          label="Lesson quizzes"
          done={status.quizzes.translated + status.quizzes.stale}
          total={status.quizzes.total}
          outstanding={quizzesOutstanding}
          note={
            status.quizzes.blocked > 0
              ? `${status.quizzes.blocked} follow their lessons automatically`
              : quizzesOutstanding > 0
                ? `${quizzesOutstanding} still to translate`
                : undefined
          }
          actionLabel={`Translate ${quizzesOutstanding} quiz${quizzesOutstanding === 1 ? "" : "zes"}`}
          onAction={() => onTranslate(language, "quizzes")}
          busy={busy}
        />
        <ActionCard
          label="Placement quiz"
          done={status.placement.translated}
          total={status.placement.total}
          outstanding={placementOutstanding}
          note={
            status.placement.total === 0
              ? "This course has no placement quiz"
              : placementOutstanding > 0
                ? `${placementOutstanding} level${placementOutstanding === 1 ? "" : "s"} still to translate`
                : undefined
          }
          actionLabel="Translate placement quiz"
          onAction={() => onTranslate(language, "placement")}
          busy={busy}
        />

        {!status.readyToOffer ? (
          <p
            className={`rounded-lg px-3 py-2 text-xs ring-1 ${
              status.offered
                ? "bg-amber-50 text-amber-900 ring-amber-200"
                : "bg-slate-50 text-slate-600 ring-slate-200"
            }`}
          >
            <span className="font-semibold">
              {status.offered
                ? `Live in ${language} with work outstanding:`
                : `Not ready to offer:`}
            </span>{" "}
            {status.blocking.join(" · ")}
            {status.offered
              ? ". Anything untranslated shows in English, and new lessons cannot be published until they are translated."
              : ""}
          </p>
        ) : !status.offered ? (
          <p className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-900 ring-1 ring-emerald-200">
            Everything published is translated. {language} is ready to offer.
          </p>
        ) : null}

        {outOfDate > 0 ? (
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 ring-1 ring-amber-200">
            <span className="font-semibold">
              {outOfDate} {outOfDate === 1 ? "item was" : "items were"} edited in English
              since translating.
            </span>
            <span>Learners keep reading the {language} as it stands.</span>
            <button
              type="button"
              onClick={() => onSyncStale(language)}
              disabled={busy}
              className="ml-auto shrink-0 rounded-lg border border-amber-300 bg-white px-2.5 py-1 font-semibold text-amber-900 transition-colors hover:bg-amber-100 disabled:opacity-50"
            >
              Sync everything out of date
            </button>
          </p>
        ) : null}
      </div>

      <div className="border-t border-slate-100">
        <div className="flex flex-wrap items-baseline gap-x-2 bg-slate-50/60 px-5 py-2">
          <h3 className="text-sm font-bold text-slate-900">Lesson by lesson</h3>
          <p className="text-xs text-slate-500">
            A lesson cannot be published until its translation and quiz are ready. Work runs
            on the worker in the background. Refresh to see progress.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[46rem] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-500">
                <th className="px-6 py-2.5 font-semibold">Lesson</th>
                <th className="px-4 py-2.5 font-semibold">Content</th>
                <th className="px-4 py-2.5 font-semibold">Quiz</th>
                <th className="px-6 py-2.5 text-right font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {byModule.map((mod) => (
                <Fragment key={mod.moduleId}>
                  <tr className="bg-slate-50/70">
                    <td
                      colSpan={4}
                      className="px-6 py-1.5 text-xs font-bold uppercase tracking-wide text-slate-500"
                    >
                      {mod.moduleTitle}
                    </td>
                  </tr>
                  {mod.rows.map((row) => {
                    const needsLesson = row.lesson === "missing";
                    const needsQuiz = row.quiz === "missing" || row.quiz === "blocked";
                    const isStale = row.lesson === "stale" || row.quiz === "stale";
                    return (
                      <tr key={row.lessonId} className="border-b border-slate-50 last:border-0">
                        <td className="px-6 py-2.5">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-800">{row.lessonTitle}</span>
                            {!row.published ? (
                              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                                Draft
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <Chip tone={LESSON_CHIP[row.lesson].tone}>
                            {LESSON_CHIP[row.lesson].label}
                          </Chip>
                        </td>
                        <td className="px-4 py-2.5">
                          <Chip tone={QUIZ_CHIP[row.quiz].tone}>{QUIZ_CHIP[row.quiz].label}</Chip>
                        </td>
                        <td className="px-6 py-2.5 text-right">
                          {row.lesson === "not_ready" ? (
                            <span className="text-xs text-slate-400">—</span>
                          ) : needsLesson || needsQuiz ? (
                            <button
                              type="button"
                              onClick={() => onTranslate(language, "lesson", { lessonId: row.lessonId })}
                              disabled={busy}
                              className="rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 disabled:opacity-50"
                            >
                              Translate
                            </button>
                          ) : isStale ? (
                            <button
                              type="button"
                              onClick={() =>
                                onTranslate(language, "lesson", {
                                  lessonId: row.lessonId,
                                  includeStale: true,
                                })
                              }
                              disabled={busy}
                              className="rounded-lg border border-amber-300 bg-white px-2.5 py-1 text-xs font-semibold text-amber-900 transition-colors hover:bg-amber-50 disabled:opacity-50"
                            >
                              Sync
                            </button>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
export default TranslationsTab;
